import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { withSize } from 'react-sizeme';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    sortRatesByFeaturedAndValue,
    SCENARIO_TYPES,
    Experiments,
} from '@ratehub/mtg-common';
import {
    Sizes,
    Colours,
    CondensedTheme,
    FormattedNumberRaisedSymbol,
    RemoveDefaultButtonStyles,
    useForm,
    PrimaryButton,
    Config,
    TooltipAnchor,
    useExperiment,
    ExperimentSegments,
    Locales,
    getLanguageFromLocale,
} from '@ratehub/base-ui';
import { PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';

import MESSAGES from '../definitions/ProductTableMessages';
import ResponsiveTable from './ResponsiveTable';
import ProductTableProviderCell from './ProductTableProviderCell';


// Note: This component had to be separated from ProductTableMortgages
//  due to the sizeme usage.
function ProductTableMortgagesTable({
    rates = [],
    handleRateClick,
    size,
    scenario,
}) {
    const intl = useIntl();
    const form = useForm();
    const isEnglish = intl.locale === Locales.ENGLISH;

    const isNarrow = size?.width <= MOBILE_SIZE;
    const PRODUCT_TABLE_FILTERED_ASYNC_DELAY = 400;

    // set showRenewalBonusColumn state to true if the experiment is active and the scenario is renewal
    const [ showRenewalBonusColumn, setShowRenewalBonusColumn ] = useState(false);

    // check if the experiment is active
    const isRenewalBonusExperimentActive = useExperiment(Experiments.mtgProductTableRenewalBonus)?.segment === ExperimentSegments.VARIANT_1;

    useEffect(() => {
        // 💩 don't do this! This will be corrected in https://ratehub.atlassian.net/browse/EVR-3751
        setTimeout(() => {
            setShowRenewalBonusColumn(isRenewalBonusExperimentActive && scenario === SCENARIO_TYPES.RENEWAL);
        }, PRODUCT_TABLE_FILTERED_ASYNC_DELAY);
    }, [
        scenario,
    ]);


    const columns = [
        ...(showRenewalBonusColumn
            ? [ {
                label: intl.formatMessage(MESSAGES.tableCashback),
                align: 'left',
            } ]
            : []
        ),
        {
            label: intl.formatMessage(MESSAGES.tableRate),
            align: 'left',
        },
        {
            label: intl.formatMessage(MESSAGES.tableProvider),
            align: 'left',
        },
        ...(!showRenewalBonusColumn
            ? [ {
                label: intl.formatMessage(MESSAGES.tablePayment),
                align: 'left',
            } ]
            : []
        ),
        {
            label: '',
            align: 'left',
            /* Wrap on small mobile screens */
            fullRowBelowWidth: 340,
        },
    ];

    const rows = sortRatesByFeaturedAndValue(rates).map(rate => {
        const renewalBonus = showRenewalBonusColumn
            ? getRenewalBonusForProvider(rate)
            : null;

        // The TooltipAnchor component created here due to FR content requiring different positioning to fit existing layout
        const TooltipAnchorComponent = (
            <If condition={Boolean(renewalBonus)}>
                <TooltipAnchor
                    data-name={`tooltip-${renewalBonus?.provider}`}
                    className="tooltip rh-ml-0_25 rh-mr-0"
                    tooltipLocation="right"
                    nubbinAlignment="middle"
                    buttonSize={isNarrow
                        ? Sizes.ICONS['2XS']
                        : Sizes.ICONS.XS
                    }
                    tooltipWidth="15em"
                    tooltipClassName="rh-p-1_5"
                    isCompact
                >
                    <p className="rh-title-2xs">
                        <FormattedMessage {...MESSAGES.switchBonusTooltipTitle} />
                    </p>
                    <p>
                        {renewalBonus?.text[getLanguageFromLocale(intl.locale)]}
                    </p>
                </TooltipAnchor>
            </If>
        );

        return ({
            analyticsObject: rate,
            htmlCellContents: [
                ...(showRenewalBonusColumn
                    ? [ {
                        content: (
                            <div className="bonus">
                                <If condition={renewalBonus}>
                                    <div className={classNames(
                                        {
                                            'rh-display-flex': isEnglish,
                                            'rh-display-inline-block' : !isEnglish,
                                        })}
                                    >
                                        {/* TODO: Add solid star icon to <CaptionedIcon /> component to replace the div below */}
                                        <div className="rh-bg-yuzu-lightest rh-border-radius-10px rh-display-flex rh-px-0_5 rh-py-0_25 rh-align-items-center">
                                            <span className="rh-fg-yuzu-darkest rh-text-xs rh-mr-0_25 bonus-icon">
                                                {'★'}
                                            </span>
                                            <span className="rh-text-xs leading-l">
                                                <FormattedMessage {...MESSAGES.pillSwitch} />
                                            </span>
                                        </div>
                                        <If condition={isEnglish}>
                                            {TooltipAnchorComponent}
                                        </If>
                                    </div>
                                    <div
                                        className={classNames('rh-m-0', {
                                            'rh-display-flex rh-pt-0_5': !isEnglish,
                                            'rh-pt-0_25': isEnglish,
                                            'rh-title-s': isNarrow,
                                            'rh-title-m': !isNarrow,
                                        })}
                                    >
                                        <FormattedNumberRaisedSymbol
                                            value={renewalBonus?.value}
                                            format="CAD0"
                                            symbolClassName={classNames('rh-mb-0', {
                                                'rh-title-xs': !isNarrow,
                                                'rh-title-2xs': isNarrow,
                                            })}
                                        />
                                        <If condition={!isEnglish}>
                                            {TooltipAnchorComponent}
                                        </If>
                                    </div>
                                </If>
                            </div>
                        ),
                        wrapperClassName: 'left-most-cell',
                    } ]
                    : []
                ),
                {
                    content: (
                        <div className="rate">
                            <If condition={rate.isFeatured}>
                                <StyledFeaturedLabel
                                    className="featured"
                                    message={intl.formatMessage(MESSAGES.featured)}
                                />
                            </If>

                            <button
                                type="button"
                                className="large rh-text-align-left weight-medium rh-fg-blueberry-dark rh-pr-0"
                                data-name="mtg-ProductTableMortgagesTable-rate"
                                onClick={() => handleRateClick(rate)}
                            >
                                <span
                                    className={classNames('text-wrapper', {
                                        'rh-title-s': isNarrow,
                                        'rh-title-m': !isNarrow,
                                    })}
                                >
                                    <FormattedNumberRaisedSymbol
                                        value={rate.value / 100}
                                        format="percent2"
                                        symbolClassName={classNames({
                                            'rh-title-xs': !isNarrow,
                                            'rh-title-2xs': isNarrow,
                                        })}
                                    />
                                </span>
                            </button>
                        </div>
                    ),
                    wrapperClassName: showRenewalBonusColumn ? 'center-cell' : 'left-most-cell',
                },
                {
                    content: (
                        <CondensedTheme>
                            <ProductTableProviderCell
                                className="provider-container"
                                rate={rate}
                                isLogoWrapped={false}
                                hasLogoBorder={true}
                            />
                        </CondensedTheme>
                    ),
                    wrapperClassName: 'center-cell',
                },
                ...(!showRenewalBonusColumn
                    ? [ {
                        content: (
                            <div className="monthlyPayment">
                                <If condition={rate.monthlyPayment}>
                                    <span
                                        className={classNames('payment-amount', 'rh-my-0', 'weight-medium', {
                                            'rh-title-m': !isNarrow,
                                            'rh-title-s': isNarrow,
                                        })}
                                    >
                                        <FormattedNumberRaisedSymbol
                                            value={rate.monthlyPayment}
                                            format="CAD0"
                                            symbolClassName={classNames({
                                                'rh-title-xs': !isNarrow,
                                                'rh-title-2xs': isNarrow,
                                            })}
                                        />
                                    </span>
                                    <If condition={form?.values?.paymentFrequency === PAYMENT_FREQUENCIES.MONTHLY}>
                                        <span
                                            className={classNames('rh-my-0', {
                                                'rh-text-m': !isNarrow,
                                                'rh-text-xs': isNarrow,
                                            })}
                                        >
                                            <FormattedMessage
                                                id="web-component.ProductTableMortgagesTableV2.monthlyPaymentPostfix"
                                                defaultMessage="/mo"
                                            />
                                        </span>
                                    </If>
                                </If>
                            </div>
                        ),
                        wrapperClassName: 'center-cell',
                    } ]
                    : []
                ),
                {
                    content: (
                        <div className="productCTAWrapper">
                            <PrimaryButton
                                size="small"
                                message={(
                                    <FormattedMessage
                                        id="web-components.ProductTableMortgages.tableCTAInquire"
                                        defaultMessage="Inquire"
                                    />
                                )}
                                onClick={() => handleRateClick(rate)}
                                data-name="product-table-mortgages-cta-click"
                            />
                        </div>
                    ),
                    wrapperClassName: 'right-most-cell',
                },
            ],
        });
    });

    return (
        <Container
            className={classNames({
                'is-narrow': isNarrow,
            })}
        >
            <ResponsiveTable
                columns={columns}
                rows={rows}
                maxRowsCollapsed={MAX_RATES_VISIBLE}
                maxRowsExpanded={rows.length}
                isAlwaysExpanded={rows.length <= MAX_RATES_VISIBLE}
                noRowDataMessage={MESSAGES.noRowDataMessage}
                isToggleButtonWithinTable
            />
        </Container>
    );
}

ProductTableMortgagesTable.propTypes = {
    rates: PropTypes.array,
    handleRateClick: PropTypes.func.isRequired,
    size: PropTypes.object.isRequired,
    scenario: PropTypes.oneOf(Object.values(SCENARIO_TYPES)),
};

function getRenewalBonusForProvider(rate) {
    // get provider slug from rate
    const rateProviderSlug = rate.provider.slug;
    // filter Config.MORTGAGE_PRODUCT_TABLE_SWITCH_BONUS by provider slug from rate
    return Config.MORTGAGE_PRODUCT_TABLE_SWITCH_BONUS.find((bonus) => bonus.provider === rateProviderSlug);
}

const MAX_RATES_VISIBLE = 6;
const MOBILE_SIZE = 510;
const CELL_PADDING_LARGE = Sizes.SPACING.ONE_AND_A_HALF;
const CELL_PADDING_MEDIUM = Sizes.SPACING.HALF;
const CELL_PADDING_SMALL = Sizes.SPACING.QUARTER;

const Container = styled.div`
    /* table-container class needed here for increased specificity */
    .table-container {
        border: 0;
        padding: 0;

        .left-most-cell,
        .right-most-cell,
        .full-width-cell,
        .center-cell {
            padding-top: ${Sizes.SPACING.ONE};
            padding-bottom: ${Sizes.SPACING.ONE};
        }

        .left-most-cell {
            padding-left: ${CELL_PADDING_LARGE};
            padding-right: ${CELL_PADDING_MEDIUM};
        }

        .center-cell {
            padding-right: ${CELL_PADDING_MEDIUM};
            padding-left: ${CELL_PADDING_MEDIUM};

            .monthlyPayment {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;

                align-items: baseline;

                .payment-amount {
                    /* Adding this here since the title class overrides the margin class */
                    margin-bottom: 0;
                }
            }
        }

        .right-most-cell {
            padding-right: ${CELL_PADDING_LARGE};
            padding-left: ${CELL_PADDING_MEDIUM};

            .productCTAWrapper {
                margin: 0 auto;
                width: 12.8rem;

                .productCTA {
                    height: ${Sizes.SPACING.THREE};
                }
            }
        }

        .bonus {
            min-width: max-content;

            .bonus-icon, .tooltip {
                align-self: center;
            }
        }

    }

    /* Narrow styles when size.width <= MOBILE_SIZE */
    &.is-narrow {
        .table-container {
            .left-most-cell {
                padding-left: ${CELL_PADDING_SMALL};
                padding-right: ${CELL_PADDING_SMALL};

                .text-wrapper {
                    font-size: ${Sizes.FONTS.XL};
                }
            }

            .center-cell {
                padding-right: ${CELL_PADDING_SMALL};
                padding-left: ${CELL_PADDING_SMALL};

                .provider-container {
                    max-width: 9rem;
                }
            }

            .right-most-cell {
                padding-right: ${CELL_PADDING_SMALL};
                padding-left: ${CELL_PADDING_SMALL};

                .productCTAWrapper {
                    width: 100%;

                    .productCTA {
                        padding-left: ${Sizes.SPACING.THREE_QUARTERS};
                        padding-right: ${Sizes.SPACING.THREE_QUARTERS};
                    }
                }
            }
        }
    }

    .large {
        ${RemoveDefaultButtonStyles};

        cursor: pointer;

        &:hover,
        &:focus {
            color: ${Colours.BLUEBERRY};

            .text-wrapper {
                box-shadow: inset 0 -2px ${Colours.BLUEBERRY};
            }
        }
    }
`;

// EVR-3047 - Google was indexing the "featured" label, so we're hiding it from crawlers
const StyledFeaturedLabel = styled.span`
    &::after {
        content: "${({ message }) => message}";
    }
`;

export default withSize({ noPlaceholder: true })(observer(ProductTableMortgagesTable));
